import { useCallback, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

export type Option = {
  value: string;
  label: string;
};

type Props = {
  title: string;
  options?: Array<Option>;
  value?: string | null;
  onChange?: (value: string | null) => void;
  isLoading?: boolean;
};

const RadioSelect = ({ title, options = [], value, onChange }: Props) => {
  const [open, setOpen] = useState<boolean>(false);

  const selectedOption = options.find(option => option.value === value);
  const displayedValue = selectedOption?.label || '';

  const handleToggleOpen = () => {
    setOpen(!open);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.(null);
  };

  const handleIconClick = (event: React.MouseEvent) => {
    if (!value) return;
    event.stopPropagation();
    handleClear(event);
  };

  const handleStopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange?.(newValue || null);
    },
    [onChange]
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        className="rounded-lg p-[10px] bg-gray-800 cursor-pointer relative text-[12px] h-[34px] inline-flex items-center gap-[20px]"
        onClick={handleToggleOpen}
      >
        <Popover.Trigger asChild>
          <div className="flex items-center gap-[20px] min-w-0 w-full">
            {!value && <span className="text-white text-[12px]">{title}</span>}
            {value && (
              <span className="text-white text-[12px] overflow-hidden">{displayedValue}</span>
            )}
            <div className="flex-shrink-0 cursor-pointer" onClick={handleIconClick}>
              {value ? (
                <X size={16} color="white" />
              ) : open ? (
                <ChevronUp size={16} color="white" />
              ) : (
                <ChevronDown size={16} color="white" />
              )}
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="bg-gray-800 text-white rounded-md overflow-hidden z-30 py-2 px-[10px] border-none outline-none"
            align="start"
            sideOffset={14}
            alignOffset={-10}
          >
            {options.length > 0 && (
              <div onClick={handleStopPropagation}>
                <RadioGroup.Root
                  value={value ?? ''}
                  onValueChange={handleValueChange}
                  className="flex flex-col"
                >
                  {options.map(option => {
                    const isSelected = value === option.value;
                    return (
                      <RadioGroup.Item
                        key={option.value}
                        value={option.value}
                        className="flex items-center gap-[10px] cursor-pointer px-3 py-2 rounded hover:bg-[#2a2d32] outline-none"
                      >
                        <div
                          className={`flex h-4 w-4 items-center justify-center rounded-full border-2
                                                    ${isSelected ? 'border-blue-500' : 'border-gray-600'}`}
                        >
                          {isSelected && (
                            <div className="h-[6px] w-[6px] rounded-full bg-blue-500" />
                          )}
                        </div>
                        <div>{option.label}</div>
                      </RadioGroup.Item>
                    );
                  })}
                </RadioGroup.Root>
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
};

export default RadioSelect;
